/** In-memory, opt-in diagnostics for physical qualification runs. No data is uploaded. */
export function createMeasurementHooks(performanceRef = globalThis.performance) {
  const startedAt = () => performanceRef?.now?.() ?? 0;
  const mark = name => {
    const markName = `browser-ar.${name}`;
    try {
      performanceRef?.clearMarks?.(markName);
      performanceRef?.mark?.(markName);
    } catch { /* diagnostics never interrupt AR */ }
  };

  let cameraTapAt = null;
  let cameraReadyAt = null;
  let firstTargetAt = null;
  let lastTargetFoundAt = null;
  // This measures only the first acquisition after cameraStart, not overlay alignment.
  let targetFoundToFirstFreshXrFrameMs = null;
  let targetAcquisitions = 0;
  let targetLosses = 0;
  let updateCallbacks = 0;
  let firstUpdateAt = null;
  let lastUpdateAt = null;
  let processedFreshFrameCount = 0;
  let firstProcessedFreshFrameAt = null;
  let lastProcessedFreshFrameAt = null;
  let lastFrameRepeatStatus = 'unavailable';
  let cameraResolution = null;
  let cameraTrackSettings = null;
  let cameraTrackStatus = 'unavailable';
  const lastTargetPose = new Map();
  let poseWindowMarkedAt = null;
  let poseWindowLastAt = null;
  let relativePoseSamples = 0;
  let relativeTranslationTotal = 0;
  let relativeTranslationMax = 0;
  let relativeRotationTotalDeg = 0;
  let relativeRotationMaxDeg = 0;
  const videoPlayers = new Map();
  let detachedVideoSnapshots = [];
  let concurrentPlayingCountHighWater = 0;
  let concurrentPlaybackDurationMs = 0;
  let concurrentPlaybackStartedAt = null;
  const concurrentPlaybackDurationsByCount = new Map();
  const concurrentPlaybackStartedByCount = new Map();

  function cameraStart() {
    cameraTapAt = startedAt();
    cameraReadyAt = null;
    firstTargetAt = null;
    lastTargetFoundAt = null;
    targetFoundToFirstFreshXrFrameMs = null;
    targetAcquisitions = 0;
    targetLosses = 0;
    updateCallbacks = 0;
    firstUpdateAt = null;
    lastUpdateAt = null;
    processedFreshFrameCount = 0;
    firstProcessedFreshFrameAt = null;
    lastProcessedFreshFrameAt = null;
    lastFrameRepeatStatus = 'unavailable';
    cameraResolution = null;
    cameraTrackSettings = null;
    cameraTrackStatus = 'unavailable';
    for (const player of videoPlayers.values()) {
      player.waitingEvents = 0;
      player.playingEvents = 0;
      player.errorEvents = 0;
      player.renewalCount = 0;
      player.lastUrlRenewalAtMs = null;
      player.isPlaying = !player.element.paused;
    }
    detachedVideoSnapshots = [];
    concurrentPlayingCountHighWater = 0;
    concurrentPlaybackDurationMs = 0;
    concurrentPlaybackStartedAt = null;
    concurrentPlaybackDurationsByCount.clear();
    concurrentPlaybackStartedByCount.clear();
    updateConcurrentPlayback();
    resetPoseWindow();
    mark('camera-start');
  }

  function cameraReady() {
    if (cameraTapAt === null || cameraReadyAt !== null) return;
    cameraReadyAt = startedAt();
    mark('camera-ready');
  }

  function targetFound() {
    const now = startedAt();
    targetAcquisitions++;
    firstTargetAt ??= now;
    if (targetFoundToFirstFreshXrFrameMs === null && lastTargetFoundAt === null) lastTargetFoundAt = now;
    mark('target-found');
  }

  function targetLost() {
    targetLosses++;
    mark('target-lost');
  }

  function resetPoseWindow() {
    poseWindowMarkedAt = null;
    poseWindowLastAt = null;
    lastTargetPose.clear();
    relativePoseSamples = 0;
    relativeTranslationTotal = 0;
    relativeTranslationMax = 0;
    relativeRotationTotalDeg = 0;
    relativeRotationMaxDeg = 0;
  }

  function markStationary() {
    resetPoseWindow();
    poseWindowMarkedAt = startedAt();
    mark('stationary-window-marked');
  }

  function observeTargetPose(detail) {
    if (poseWindowMarkedAt === null || !detail?.name) return;
    const position = detail.position;
    const rotation = detail.rotation;
    const current = {
      position: [Number(position?.x), Number(position?.y), Number(position?.z)],
      rotation: [Number(rotation?.x), Number(rotation?.y), Number(rotation?.z), Number(rotation?.w)],
    };
    if (![...current.position, ...current.rotation].every(Number.isFinite)) return;
    const previous = lastTargetPose.get(detail.name);
    if (previous) {
      const translation = Math.hypot(...current.position.map((value, index) => value - previous.position[index]));
      const dot = Math.min(1, Math.abs(current.rotation.reduce((sum, value, index) => sum + value * previous.rotation[index], 0)));
      const rotationDegrees = 2 * Math.acos(dot) * 180 / Math.PI;
      relativePoseSamples++;
      relativeTranslationTotal += translation;
      relativeTranslationMax = Math.max(relativeTranslationMax, translation);
      relativeRotationTotalDeg += rotationDegrees;
      relativeRotationMaxDeg = Math.max(relativeRotationMaxDeg, rotationDegrees);
      poseWindowLastAt = startedAt();
    }
    lastTargetPose.set(detail.name, current);
  }

  function clearTargetPose(name) {
    if (name) lastTargetPose.delete(name);
  }

  function update(input = {}) {
    const now = startedAt();
    updateCallbacks++;
    firstUpdateAt ??= now;
    lastUpdateAt = now;

    const frameStartResult = input?.frameStartResult;
    if (!frameStartResult || typeof frameStartResult.repeatFrame !== 'boolean') {
      lastFrameRepeatStatus = 'unavailable';
      return;
    }
    lastFrameRepeatStatus = frameStartResult.repeatFrame ? 'repeat' : 'fresh';
    if (frameStartResult.repeatFrame) return;

    processedFreshFrameCount++;
    firstProcessedFreshFrameAt ??= now;
    lastProcessedFreshFrameAt = now;
    if (lastTargetFoundAt !== null && targetFoundToFirstFreshXrFrameMs === null) {
      targetFoundToFirstFreshXrFrameMs = now - lastTargetFoundAt;
      lastTargetFoundAt = null;
    }
  }

  function cameraSize(dimensions) {
    const width = Number(dimensions?.videoWidth ?? dimensions?.width);
    const height = Number(dimensions?.videoHeight ?? dimensions?.height);
    if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
      cameraResolution = {widthPx: width, heightPx: height};
    }
  }

  function observeCameraStream(stream) {
    const track = stream?.getVideoTracks?.()[0] ?? null;
    try {
      cameraTrackSettings = track?.getSettings?.() ?? null;
      cameraTrackStatus = track?.readyState ?? (track ? 'ready-state-unavailable' : 'no-video-track');
    } catch {
      cameraTrackSettings = null;
      cameraTrackStatus = 'settings-unavailable';
    }
  }

  function watchVideo(element) {
    watchVideos(element ? [{videoId: 'video', element}] : []);
  }

  function unwatchVideo() {
    unwatchVideos();
  }

  function updateConcurrentPlayback() {
    const now = startedAt();
    let playingCount = 0;
    for (const player of videoPlayers.values()) if (player.isPlaying) playingCount++;
    concurrentPlayingCountHighWater = Math.max(concurrentPlayingCountHighWater, playingCount);
    if (playingCount >= 2) {
      concurrentPlaybackStartedAt ??= now;
    } else if (concurrentPlaybackStartedAt !== null) {
      concurrentPlaybackDurationMs += Math.max(0, now - concurrentPlaybackStartedAt);
      concurrentPlaybackStartedAt = null;
    }
    for (let minimumVideoCount = 2; minimumVideoCount <= concurrentPlayingCountHighWater; minimumVideoCount++) {
      if (!concurrentPlaybackDurationsByCount.has(minimumVideoCount)) {
        concurrentPlaybackDurationsByCount.set(minimumVideoCount, 0);
      }
      const started = concurrentPlaybackStartedByCount.get(minimumVideoCount);
      if (playingCount >= minimumVideoCount) {
        if (started === undefined) concurrentPlaybackStartedByCount.set(minimumVideoCount, now);
      } else if (started !== undefined) {
        concurrentPlaybackDurationsByCount.set(minimumVideoCount, Math.max(
          concurrentPlaybackDurationsByCount.get(minimumVideoCount),
          Math.max(0, now - started),
        ));
        concurrentPlaybackStartedByCount.delete(minimumVideoCount);
      }
    }
  }

  function watchVideos(entries) {
    unwatchVideos();
    detachedVideoSnapshots = [];
    for (const entry of entries ?? []) {
      if (!entry?.element || typeof entry.videoId !== 'string' || videoPlayers.has(entry.videoId)) continue;
      const player = {
        videoId: entry.videoId,
        element: entry.element,
        waitingEvents: 0,
        playingEvents: 0,
        errorEvents: 0,
        renewalCount: 0,
        lastUrlRenewalAtMs: null,
        isPlaying: !entry.element.paused,
        listeners: [],
      };
      const listen = (name, callback) => {
        entry.element.addEventListener?.(name, callback);
        player.listeners.push([name, callback]);
      };
      listen('waiting', () => { player.waitingEvents++; player.isPlaying = false; updateConcurrentPlayback(); });
      listen('playing', () => { player.playingEvents++; player.isPlaying = true; updateConcurrentPlayback(); });
      listen('pause', () => { player.isPlaying = false; updateConcurrentPlayback(); });
      listen('ended', () => { player.isPlaying = false; updateConcurrentPlayback(); });
      listen('error', () => { player.errorEvents++; player.isPlaying = false; updateConcurrentPlayback(); });
      videoPlayers.set(entry.videoId, player);
    }
    updateConcurrentPlayback();
  }

  function unwatchVideos() {
    if (videoPlayers.size > 0) detachedVideoSnapshots = [...videoPlayers.values()].map(snapshotVideoPlayer);
    for (const player of videoPlayers.values()) {
      for (const [event, listener] of player.listeners) player.element.removeEventListener?.(event, listener);
    }
    videoPlayers.clear();
    if (concurrentPlaybackStartedAt !== null) {
      concurrentPlaybackDurationMs += Math.max(0, startedAt() - concurrentPlaybackStartedAt);
      concurrentPlaybackStartedAt = null;
    }
    const stoppedAt = startedAt();
    for (const [minimumVideoCount, started] of concurrentPlaybackStartedByCount) {
      concurrentPlaybackDurationsByCount.set(minimumVideoCount, Math.max(
        concurrentPlaybackDurationsByCount.get(minimumVideoCount),
        Math.max(0, stoppedAt - started),
      ));
      concurrentPlaybackStartedByCount.delete(minimumVideoCount);
    }
  }

  function noteVideoRenewal(videoId) {
    const player = videoPlayers.get(videoId);
    if (player) {
      player.renewalCount++;
      player.lastUrlRenewalAtMs = startedAt();
    }
  }

  function snapshotVideoPlayer(player) {
    const video = player.element;
    let quality = null;
    try { quality = video.getVideoPlaybackQuality?.() ?? null; } catch { /* diagnostics never interrupt AR */ }
    return {
      videoId: player.videoId,
      widthPx: video.videoWidth || 0,
      heightPx: video.videoHeight || 0,
      durationSeconds: Number.isFinite(video.duration) ? video.duration : null,
      positionSeconds: Number.isFinite(video.currentTime) ? video.currentTime : null,
      paused: Boolean(video.paused),
      muted: Boolean(video.muted),
      waitingEvents: player.waitingEvents,
      playingEvents: player.playingEvents,
      errorEvents: player.errorEvents,
      decodedFrames: quality?.totalVideoFrames ?? null,
      droppedFrames: quality?.droppedVideoFrames ?? null,
      renewalCount: player.renewalCount,
      lastUrlRenewalAtMs: player.lastUrlRenewalAtMs,
    };
  }

  function snapshot() {
    const updateElapsedMs = firstUpdateAt !== null && lastUpdateAt !== null && lastUpdateAt > firstUpdateAt
      ? lastUpdateAt - firstUpdateAt
      : null;
    const freshElapsedMs = firstProcessedFreshFrameAt !== null && lastProcessedFreshFrameAt !== null && lastProcessedFreshFrameAt > firstProcessedFreshFrameAt
      ? lastProcessedFreshFrameAt - firstProcessedFreshFrameAt
      : null;
    const poseWindowDurationMs = poseWindowMarkedAt !== null
      ? (poseWindowLastAt ?? startedAt()) - poseWindowMarkedAt
      : null;
    const overlapDuration = concurrentPlaybackStartedAt === null
      ? concurrentPlaybackDurationMs
      : concurrentPlaybackDurationMs + Math.max(0, startedAt() - concurrentPlaybackStartedAt);
    const overlapDurationsByCount = [...concurrentPlaybackDurationsByCount.entries()]
      .sort(([left], [right]) => left - right)
      .map(([minimumVideoCount, durationMs]) => ({
        minimumVideoCount,
        durationMs: Math.max(
          durationMs,
          Math.max(0, startedAt() - (concurrentPlaybackStartedByCount.get(minimumVideoCount) ?? startedAt())),
        ),
      }));
    const videos = videoPlayers.size > 0
      ? [...videoPlayers.values()].map(snapshotVideoPlayer)
      : detachedVideoSnapshots;
    const primaryVideo = videos[0] ?? null;
    return {
      cameraTapToReadyMs: cameraTapAt !== null && cameraReadyAt !== null ? cameraReadyAt - cameraTapAt : null,
      cameraReadyToFirstTargetMs: cameraReadyAt !== null && firstTargetAt !== null ? firstTargetAt - cameraReadyAt : null,
      cameraTapToFirstTargetMs: cameraTapAt !== null && firstTargetAt !== null ? firstTargetAt - cameraTapAt : null,
      camera: {
        resolution: cameraResolution ?? (cameraTrackSettings?.width && cameraTrackSettings?.height
          ? {widthPx: cameraTrackSettings.width, heightPx: cameraTrackSettings.height}
          : null),
        resolutionSource: cameraResolution ? 'xr8-video-size' : (cameraTrackSettings?.width && cameraTrackSettings?.height ? 'media-track-settings' : 'unavailable'),
        trackResolution: cameraTrackSettings?.width && cameraTrackSettings?.height
          ? {widthPx: cameraTrackSettings.width, heightPx: cameraTrackSettings.height}
          : null,
        trackStatus: cameraTrackStatus,
      },
      targetAcquisitions,
      targetLosses,
      targetFoundToFirstFreshXrFrameMs,
      relativePose: {
        marked: poseWindowMarkedAt !== null,
        windowDurationMs: poseWindowDurationMs,
        samples: relativePoseSamples,
        translationDeltaMeanXrUnits: relativePoseSamples ? relativeTranslationTotal / relativePoseSamples : null,
        translationDeltaMaxXrUnits: relativePoseSamples ? relativeTranslationMax : null,
        rotationDeltaMeanDegrees: relativePoseSamples ? relativeRotationTotalDeg / relativePoseSamples : null,
        rotationDeltaMaxDegrees: relativePoseSamples ? relativeRotationMaxDeg : null,
        interpretation: 'relative frame-to-frame XR8 pose change in the manually marked window; no absolute registration ground truth',
      },
      xrUpdateCallbacks: updateCallbacks,
      xrUpdateCallbackFps: updateElapsedMs !== null ? ((updateCallbacks - 1) * 1000) / updateElapsedMs : null,
      processedFreshFrameCount,
      processedFreshFramesPerSecond: freshElapsedMs !== null
        ? ((processedFreshFrameCount - 1) * 1000) / freshElapsedMs
        : null,
      lastFrameRepeatStatus,
      // Keep the single-video projection consumed by the qualification form alongside all players.
      video: primaryVideo,
      videos,
      concurrentPlayingCountHighWater,
      concurrentPlaybackDurationMs: overlapDuration,
      concurrentPlaybackDurationsByCount: overlapDurationsByCount,
    };
  }

  return {
    cameraReady,
    cameraResolution: cameraSize,
    cameraStart,
    clearTargetPose,
    markStationary,
    noteVideoRenewal,
    observeCameraStream,
    observeTargetPose,
    resetPoseWindow,
    snapshot,
    targetFound,
    targetLost,
    unwatchVideo,
    unwatchVideos,
    update,
    watchVideo,
    watchVideos,
  };
}
